import React, { RefObject, useContext } from "react";
import {
  DrugCostInfo,
  DrugInfo,
  Plan,
  PlanDetailsPageSection,
  PlanType,
  PrescriptionDrug,
} from "../../../@types";
import { PlanDetailSection } from "../components";
import { shouldSuppressDrugPricing, useTranslate } from "../../../helpers";
import { PharmaciesTable } from "./PharmaciesTable";
import { CostsByCoveragePhaseTable } from "./CostsByCoveragePhaseTable";
import { useGetPharmacies } from "./drugCoverageHelpers";
import EstimatedMonthlyCosts from "./EstimatedMonthlyCosts";
import { EstimatedTotalDrugAndPremiumCostTable } from "./EstimatedTotalDrugAndPremiumCostTable";
import { YearlyDrugCostsByPharmacyTable } from "./YearlyDrugCostsByPharmacyTable";
import { PartBDrugsTable } from "./PartBDrugsTable";
import { MyDrugListTable } from "./MyDrugListTable";
import { OtherDrugInformationTable } from "./OtherDrugInformationTable";
import {
  ToggleMoreBenefits,
  ToggleMoreBenefitsProps,
} from "../components/ToggleMoreBenefits";
import { CostsByDrugTierTable } from "./CostsByDrugTierTable";
import { linkToPrescriptionCostHelp } from "../../../helpers/CONSTANTS";
import { AppStore } from "../../../app/store";
import { Alert } from "@cmsgov/ds-medicare-gov";

export const moreDrugCoverageButtonTextKey = "plan_terms.drug_coverage.open";

export const DrugCoverageSection: React.FunctionComponent<{
  plan: Plan;
  drugCostInfo: DrugCostInfo;
  drugInfo: DrugInfo[];
  prescriptions: PrescriptionDrug[];
  sectionRef: RefObject<HTMLDivElement>;
}> = ({ plan, drugCostInfo, sectionRef, prescriptions, drugInfo }) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  const pharmacies = useGetPharmacies(drugCostInfo);
  const hasPharmacies = pharmacies.length > 0;
  const hasPrescriptions = prescriptions.length > 0;

  const suppressDrugPricing = shouldSuppressDrugPricing(plan.redactions);

  const OtherTablesWrapper =
    hasPrescriptions && !suppressDrugPricing
      ? ToggleMoreBenefits
      : React.Fragment;

  const otherTablesWrapperProps =
    hasPrescriptions && !suppressDrugPricing
      ? {
          buttonTextToOpen: t(moreDrugCoverageButtonTextKey),
          buttonTextToCollapse: t("plan_terms.drug_coverage.collapse"),
          section: "collapse-more-drug-coverage",
        }
      : // Coerce typing to satisfy ToggleMoreBenefits. The empty object is for
        // the Fragment wrapper in the case that the conditions aren't met
        ({} as ToggleMoreBenefitsProps);

  return (
    <PlanDetailSection
      className="mct-print-break-before--page"
      id={PlanDetailsPageSection.DRUG_COVERAGE}
      title={t("plan_details.drug_coverage")}
      sectionRef={sectionRef}
      headingAddOn={
        <a
          className="ds-u-color--secondary"
          target="_blank"
          href={linkToPrescriptionCostHelp(language)}
          rel="noopener noreferrer"
        >
          {t("plan_details.reduce_drug_cost_link")}
        </a>
      }
    >
      {/* Tables go here, take one of or both plan and drugCostInfo data */}

      {/* Pharmacies table - Must have prescriptions and pharmacies unless using Lowest Cost Pharmacy feature and not viewing a MA plan without drug coverage */}
      {(plan.plan_type !== PlanType.MA ||
        (hasPharmacies && hasPrescriptions)) && (
        <PharmaciesTable drugCostInfo={drugCostInfo} plan={plan} />
      )}

      {suppressDrugPricing ? (
        <div className="PlanDetailsSection__alert-container">
          <Alert
            variation="warn"
            heading={t("suppressed_plan_drug_pricing.alert.title")}
            hideIcon
          >
            {t("suppressed_plan_drug_pricing.alert.content")}
          </Alert>
        </div>
      ) : (
        <>
          {/* Yearly Drug Cost By Pharmacy - (New table) Must have prescriptions and pharmacies */}
          {hasPharmacies && hasPrescriptions && (
            <YearlyDrugCostsByPharmacyTable
              plan={plan}
              drugCostInfo={drugCostInfo}
            />
          )}
          {/* Estimated Total Drug Cost + Premium Cost - Must have prescriptions and pharmacies (I think) */}
          {hasPharmacies && hasPrescriptions && (
            <EstimatedTotalDrugAndPremiumCostTable
              plan={plan}
              drugCostInfo={drugCostInfo}
            />
          )}
          {hasPrescriptions && (
            <EstimatedMonthlyCosts plan={plan} drugCostInfo={drugCostInfo} />
          )}
          {/* Estimated Total Monthly Drug Cost - Must have prescriptions and pharmacies */}
          {hasPrescriptions && hasPharmacies && (
            <CostsByCoveragePhaseTable
              drugCostInfo={drugCostInfo}
              plan={plan}
            />
          )}
        </>
      )}

      <OtherTablesWrapper {...otherTablesWrapperProps}>
        {/* Costs By Drug Tier - Always shown unless drug prices are suppressed */}
        {plan.abstract_benefits && !suppressDrugPricing && (
          <CostsByDrugTierTable abstractBenefits={plan.abstract_benefits} />
        )}
        {/* Other Drug Information - Must have prescriptions */}
        {hasPrescriptions && (
          <OtherDrugInformationTable
            {...{
              drugCostInfo,
              drugInfo,
              prescriptions,
            }}
          />
        )}

        {/* My Drug List - Must have prescriptions */}
        {hasPrescriptions && <MyDrugListTable prescriptions={prescriptions} />}

        {/* Part B Drugs - Always shown */}
        <PartBDrugsTable plan={plan} />
      </OtherTablesWrapper>
    </PlanDetailSection>
  );
};
